import React from "react";

interface State {
  hasError: boolean;
  errorMessage: string;
  componentStack: string;
}

/**
 * Catches render-phase errors and displays the error message + component stack
 * directly on screen. The component stack is NOT minified even in production,
 * making it useful for tracking down errors like #185 (max update depth).
 */
class ErrorBoundary extends React.Component<React.PropsWithChildren<{ fallback?: React.ReactNode }>, State> {
  state: State = { hasError: false, errorMessage: "", componentStack: "" };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message, componentStack: "" };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Caught error:", error.message);
    console.error("[ErrorBoundary] Component stack:", info.componentStack);
    console.error("[ErrorBoundary] Full error:", error);
    this.setState({ componentStack: info.componentStack ?? "" });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{ padding: 24, color: "red", fontFamily: "monospace" }}>
          <strong>Something went wrong.</strong>
          {this.state.errorMessage && (
            <pre style={{ background: "#fff0f0", padding: 12, borderRadius: 4, overflowX: "auto", whiteSpace: "pre-wrap" }}>
              {this.state.errorMessage}
            </pre>
          )}
          {this.state.componentStack && (
            <>
              <strong>Component stack (not minified):</strong>
              <pre style={{ background: "#f5f5f5", padding: 12, borderRadius: 4, overflowX: "auto", whiteSpace: "pre-wrap", fontSize: 12 }}>
                {this.state.componentStack}
              </pre>
            </>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
